import { useQuery } from '@tanstack/react-query';
import { guidedUseCaseService } from '@/services/guided-use-case-service';
import type { GuidedCaseMeta } from './guided-case';

type Props = {
  value: GuidedCaseMeta;
  onChange: (next: GuidedCaseMeta) => void;
};

/**
 * @param {Props} props - the guided fields on the form and a change handler
 * @returns {JSX.Element} the guided-case section of the test-case form
 */
export function GuidedCaseFields({ value, onChange }: Props) {
  const { data: canvases } = useQuery({
    queryKey: ['guided-canvases'],
    queryFn: () => guidedUseCaseService.listCanvases(),
  });

  const { data: canvas } = useQuery({
    queryKey: ['guided-canvas', value.canvas_key],
    queryFn: () => guidedUseCaseService.getCanvas(value.canvas_key as string),
    enabled: !!value.canvas_key,
  });

  const elements = (canvas?.data.elements ?? []).filter((element) => element.source !== 'header');
  const undefinedElement = elements.find((e) => e.slot === value.slot && !e.authored);

  return (
    <div className="rounded-md border bg-muted/30 p-4 space-y-4">
      <p className="text-xs text-muted-foreground">
        Kaiya grades the draft below against the definition of good authored for this element.
        The score is whether it reached the same verdict you did.
      </p>

      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="text-sm font-medium">Canvas</label>
          <select
            className="w-full mt-1 px-3 py-2 border rounded-md text-sm"
            value={value.canvas_key ?? ''}
            onChange={(e) => onChange({ ...value, canvas_key: e.target.value || undefined, slot: undefined })}
          >
            <option value="">Select a canvas</option>
            {(canvases?.data ?? []).map((c) => (
              <option key={c.canvas_key} value={c.canvas_key}>
                {c.title ?? c.canvas_key}
              </option>
            ))}
          </select>
        </div>

        <div>
          <label className="text-sm font-medium">Element</label>
          <select
            className="w-full mt-1 px-3 py-2 border rounded-md text-sm"
            disabled={!value.canvas_key}
            value={value.slot ?? ''}
            onChange={(e) => onChange({ ...value, slot: e.target.value || undefined })}
          >
            <option value="">{value.canvas_key ? 'Select an element' : 'Pick a canvas first'}</option>
            {elements.map((element) => (
              <option key={element.slot} value={element.slot}>
                {element.title ?? element.slot}
                {element.authored ? '' : ' — no definition yet'}
              </option>
            ))}
          </select>
        </div>
      </div>

      <div>
        <label className="text-sm font-medium">The verdict you reached on this draft</label>
        <select
          className="w-full mt-1 px-3 py-2 border rounded-md text-sm"
          value={value.expected_criteria_met === undefined ? '' : String(value.expected_criteria_met)}
          onChange={(e) =>
            onChange({
              ...value,
              expected_criteria_met: e.target.value === '' ? undefined : e.target.value === 'true',
            })
          }
        >
          <option value="">Select a verdict</option>
          <option value="true">It meets the completion criteria</option>
          <option value="false">It does not meet the completion criteria</option>
        </select>
      </div>

      {undefinedElement && (
        <p className="text-xs text-warning-700">
          {undefinedElement.title ?? undefinedElement.slot} has no definition of good yet, so a run against it will error
          rather than score. Author it under Guided Use Cases first.
        </p>
      )}
    </div>
  );
}
